import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Monitor, ArrowLeft, RefreshCw, UserPlus, Search, AlertTriangle,
  Info, Clock, ChevronLeft, ChevronRight, Download, CalendarDays,
  LogIn, LogOut, Wifi, WifiOff, Bell, BellOff, ShieldBan, Merge,
  Trash2, X, Unlink, RotateCcw, ExternalLink, Settings, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
}

function MonthPicker({ value, onChange }) {
  const d = new Date(value + "-15");
  const label = d.toLocaleString("default", { month: "long", year: "numeric" });
  const prev = () => {
    const p = new Date(d);
    p.setMonth(p.getMonth() - 1);
    onChange(`${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, "0")}`);
  };
  const next = () => {
    const n = new Date(d);
    n.setMonth(n.getMonth() + 1);
    onChange(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`);
  };
  return (
    <div className="flex items-center gap-1" data-testid="month-picker">
      <button onClick={prev} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-testid="month-prev">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className="text-white font-semibold text-sm min-w-[130px] text-center">{label}</span>
      <button onClick={next} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-testid="month-next">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function DayPills({ month, selectedDate, onSelect }) {
  const daysInMonth = new Date(parseInt(month.split("-")[0]), parseInt(month.split("-")[1]), 0).getDate();
  const today = localDateStr(new Date());
  const pills = [];
  for (let i = 1; i <= daysInMonth; i++) {
    const dateStr = `${month}-${String(i).padStart(2, "0")}`;
    const dayLabel = new Date(dateStr + "T12:00:00").toLocaleDateString("default", { weekday: "short" });
    const isSelected = selectedDate === dateStr;
    const isToday = dateStr === today;
    pills.push(
      <button
        key={dateStr}
        onClick={() => onSelect(isSelected ? null : dateStr)}
        className={`shrink-0 flex flex-col items-center px-2.5 py-1.5 rounded-lg text-xs transition-all ${
          isSelected
            ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/30"
            : isToday
            ? "bg-white/15 text-white border border-indigo-400/40"
            : "bg-white/[0.05] text-white/50 hover:bg-white/10 hover:text-white/80"
        }`}
        data-testid={`day-pill-${dateStr}`}
      >
        <span className="font-medium">{i}</span>
        <span className="text-[9px] opacity-70">{dayLabel}</span>
      </button>
    );
  }
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-thin" data-testid="day-pills">
      {pills}
    </div>
  );
}

function formatDuration(secs) {
  if (secs === null || secs === undefined) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDT(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function SessionCard({ s, isActive, onAssign, onRemoveMapping, onClose, onDelete, employees, mappingId, mappingName, setMappingName, mappingEmployeeId, setMappingEmployeeId, saveMapping, setMappingId, isBlocked, onToggleBlock }) {
  return (
    <div
      className={`bg-white/[0.04] border rounded-xl p-3.5 ${
        isActive ? "border-emerald-500/40" : "border-white/[0.08]"
      }`}
      data-testid={`session-card-${s.id}`}
    >
      {/* Top row: worker + status */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`w-2 h-2 rounded-full shrink-0 ${isActive ? "bg-emerald-400 animate-pulse" : "bg-white/20"}`} />
        <span className={`font-semibold text-sm ${isBlocked ? "text-red-300 line-through" : "text-white"}`}>
          {s.worker_name || s.alias || `AnyDesk ${s.anydesk_id}`}
        </span>
        {s.anydesk_id && (
          <span className="text-[10px] text-white/40 font-mono bg-white/[0.05] px-1.5 py-0.5 rounded">{s.anydesk_id}</span>
        )}
        {isActive && <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full">LIVE</span>}
        {s.auth_method === "REJECTED" && <span className="text-[10px] font-bold text-red-400 bg-red-500/15 px-2 py-0.5 rounded-full">REJECTED</span>}
        {isBlocked && <span className="text-[10px] font-bold text-red-400 bg-red-500/15 px-2 py-0.5 rounded-full">BLOCKED</span>}
        {/* Block/Unblock toggle */}
        {s.anydesk_id && (
          <button
            onClick={() => onToggleBlock(s.anydesk_id, s.worker_name || s.alias || s.anydesk_id)}
            className={`p-1 rounded-lg transition-colors ${
              isBlocked
                ? "text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20"
                : "text-white/20 hover:text-amber-300 hover:bg-amber-500/10"
            }`}
            data-testid={`block-toggle-${s.anydesk_id}`}
            title={isBlocked ? "Unblock this user" : "Block this user"}
          >
            <ShieldBan className="w-3.5 h-3.5" />
          </button>
        )}
        {!s.worker_name ? (
          <button
            onClick={() => onAssign(s.anydesk_id)}
            className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"
            data-testid={`assign-worker-${s.anydesk_id}`}
          >
            <UserPlus className="w-3 h-3" /> Assign
          </button>
        ) : (
          <span className="flex items-center gap-1.5">
            <button
              onClick={() => onAssign(s.anydesk_id)}
              className="text-[10px] text-white/30 hover:text-indigo-300 transition-colors"
              data-testid={`edit-worker-${s.anydesk_id}`}
            >
              edit
            </button>
            <button
              onClick={() => onRemoveMapping(s.anydesk_id)}
              className="text-[10px] text-white/20 hover:text-red-300 transition-colors"
              data-testid={`remove-worker-${s.anydesk_id}`}
              title="Remove assignment"
            >
              <Unlink className="w-2.5 h-2.5" />
            </button>
          </span>
        )}
        <span className="text-[11px] text-white/30 ml-auto">{s.host}</span>
      </div>

      {/* Session times row */}
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50 pl-4">
        <span className="flex items-center gap-1"><Wifi className="w-3 h-3 text-emerald-400/70" /> {formatTime(s.started_at)}</span>
        <span className="flex items-center gap-1"><WifiOff className="w-3 h-3 text-red-400/70" /> {s.ended_at ? formatTime(s.ended_at) : (isActive ? <span className="text-emerald-400">Active</span> : "—")}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-white/40" /> {formatDuration(s.duration_seconds)}</span>
      </div>

      {/* Time entry cross-reference */}
      {s.time_entry && (
        <div className="mt-2 ml-4 px-3 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-lg text-xs">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-white/70">
            <span className="flex items-center gap-1">
              <LogIn className="w-3 h-3 text-indigo-300" />
              Clocked in: <span className="text-white/90">{formatTime(s.time_entry.clock_in)}</span>
            </span>
            <span className="flex items-center gap-1">
              <LogOut className="w-3 h-3 text-indigo-300" />
              Clocked out: <span className="text-white/90">{s.time_entry.clock_out ? formatTime(s.time_entry.clock_out) : "Still in"}</span>
            </span>
            {s.time_entry.total_hours != null && (
              <span className="text-white/90">{s.time_entry.total_hours.toFixed(2)}h logged</span>
            )}
            {s.time_entry.anydesk_auto_clocked_out && (
              <span className="text-amber-300 flex items-center gap-1">
                <Monitor className="w-3 h-3" /> Auto clock-out
              </span>
            )}
            {s.time_entry.admin_clocked && (
              <span className="text-sky-300">Admin clocked</span>
            )}
          </div>
        </div>
      )}
      {s.employee_id && !s.time_entry && (
        <div className="mt-2 ml-4 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-200/80">
          No matching clock-in for this session
        </div>
      )}

      {/* Action buttons: Close/Delete */}
      {s.anydesk_id && (
        <div className="mt-2.5 ml-4 flex flex-wrap gap-2">
          {isActive && (
            <button
              onClick={() => onClose(s.id)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-medium hover:bg-amber-500/20 transition-colors"
              data-testid={`close-session-btn-${s.id}`}
            >
              <Clock className="w-3 h-3" /> Mark Ended
            </button>
          )}
          <button
            onClick={() => onDelete(s.id)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-white/20 text-xs hover:text-red-300 hover:bg-red-500/10 transition-colors ml-auto"
            data-testid={`delete-session-btn-${s.id}`}
            title="Delete this session record"
          >
            &times;
          </button>
        </div>
      )}

      {/* Mapping inline form */}
      {mappingId === s.anydesk_id && (
        <div className="mt-3 space-y-2">
          <select
            value={mappingEmployeeId}
            onChange={(e) => {
              setMappingEmployeeId(e.target.value);
              const emp = employees.find((emp2) => emp2.id === e.target.value);
              if (emp) setMappingName(emp.name);
            }}
            className="w-full h-9 rounded-lg bg-white/10 border border-white/20 text-white text-sm px-2 [&>option]:text-black"
            data-testid="worker-employee-select"
          >
            <option value="">Link to employee (enables cross-check)</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>
            ))}
          </select>
          <div className="flex gap-2">
            <Input
              value={mappingName}
              onChange={(e) => setMappingName(e.target.value)}
              placeholder="Display name"
              className="h-8 text-sm bg-white/10 border-white/20 text-white placeholder:text-white/40"
              data-testid="worker-name-input"
              onKeyDown={(e) => e.key === "Enter" && saveMapping(s.anydesk_id)}
            />
            <Button size="sm" className="h-8 bg-indigo-500 hover:bg-indigo-600" onClick={() => saveMapping(s.anydesk_id)} data-testid="worker-name-save">Save</Button>
            <Button size="sm" variant="ghost" className="h-8 text-white/60" onClick={() => { setMappingId(null); setMappingEmployeeId(""); }}>Cancel</Button>
            {s.worker_name && (
              <Button size="sm" variant="ghost" className="h-8 text-red-300 hover:text-red-200 hover:bg-red-500/10" onClick={() => onRemoveMapping(s.anydesk_id)} data-testid="worker-remove-btn">
                <Unlink className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RemoteSessionsPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [tab, setTab] = useState("sessions");
  const [month, setMonth] = useState(localDateStr(now).slice(0, 7));
  const [selectedDate, setSelectedDate] = useState(localDateStr(new Date()));
  const [sessions, setSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [mappingId, setMappingId] = useState(null);
  const [mappingName, setMappingName] = useState("");
  const [mappingEmployeeId, setMappingEmployeeId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [silenced, setSilenced] = useState(false);
  const [showShutdownModal, setShowShutdownModal] = useState(false);
  const [blockedIds, setBlockedIds] = useState(new Set());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [watcherHealth, setWatcherHealth] = useState(null);
  const [connectInfo, setConnectInfo] = useState({ anydesk_address: "", anydesk_password: "" });
  const [showConnectSetup, setShowConnectSetup] = useState(false);
  const [editAddress, setEditAddress] = useState("");
  const [editLabel, setEditLabel] = useState("");


  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const dateParam = selectedDate ? `&date=${selectedDate}` : `&month=${month}`;
      const [sessRes, flagRes] = await Promise.all([
        axios.get(`${API}/remote-sessions?limit=500${dateParam}`, getAuthHeader()),
        axios.get(`${API}/remote-sessions/cross-check`, getAuthHeader()).catch(() => ({ data: { flags: [] } }))
      ]);
      setSessions(sessRes.data.sessions || []);
      setFlags(flagRes.data.flags || []);
      setLastUpdated(new Date());
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) { navigate("/login"); return; }
      if (!silent) toast.error("Failed to load remote sessions");
    } finally { if (!silent) setLoading(false); }
  }, [navigate, month, selectedDate]);

  const fetchAlerts = useCallback(async () => {
    try {
      const dateParam = selectedDate ? `&date=${selectedDate}` : `&month=${month}`;
      const res = await axios.get(`${API}/remote-sessions/alerts?limit=200${dateParam}`, getAuthHeader());
      setAlerts(res.data.alerts || []);
    } catch { setAlerts([]); }
  }, [month, selectedDate]);

  useEffect(() => {
    if (!localStorage.getItem("token")) { navigate("/login"); return; }
    fetchData();
    fetchAlerts();
    fetchSilenceStatus();
    fetchBlocklist();
    fetchWatcherHealth();
    fetchHostAddresses();

    axios.get(`${API}/admin/employees`, getAuthHeader())
      .then((res) => setEmployees((Array.isArray(res.data) ? res.data : res.data.employees || []).filter((e) => e.role !== "admin")))
      .catch(() => {});
    // Auto-refresh every 5 seconds for responsive monitoring
    const poll = setInterval(() => {
      fetchData(true);
      fetchAlerts();
      fetchSilenceStatus();
      fetchWatcherHealth();
    }, 5000);
    return () => clearInterval(poll);
  }, [fetchData, fetchAlerts, navigate]);

  const fetchSilenceStatus = async () => {
    try {
      const res = await axios.get(`${API}/remote-sessions/notification-status`, getAuthHeader());
      setSilenced(res.data.silenced);
    } catch { /* ignore */ }
  };

  const fetchBlocklist = async () => {
    try {
      const res = await axios.get(`${API}/remote-sessions/blocklist`, getAuthHeader());
      const ids = new Set((res.data.blocklist || []).map(b => b.anydesk_id));
      setBlockedIds(ids);
    } catch { /* ignore */ }
  };

  const fetchWatcherHealth = async () => {
    try {
      const res = await axios.get(`${API}/remote-sessions/watcher-health`, getAuthHeader());
      const watchers = res.data.watchers || [];
      setWatcherHealth(watchers.length > 0 ? watchers[0] : null);
    } catch { /* ignore */ }
  };

  const fetchHostAddresses = async () => {
    try {
      const res = await axios.get(`${API}/remote-sessions/connect-info`, getAuthHeader());
      setConnectInfo(res.data);
    } catch { /* ignore */ }
  };

  const saveHostAddress = async () => {
    if (!editAddress.trim()) return;
    try {
      await axios.put(`${API}/remote-sessions/connect-info`, {
        anydesk_address: editAddress.trim(),
        anydesk_password: editLabel.trim(),
      }, getAuthHeader());
      setConnectInfo({ anydesk_address: editAddress.trim(), anydesk_password: editLabel.trim() });
      setShowConnectSetup(false);
      toast.success("AnyDesk connection info saved");
    } catch { toast.error("Failed to save"); }
  };

  const openAnyDesk = (address) => {
    const cleaned = address.replace(/\s/g, "");
    window.open(`anydesk:${cleaned}`, "_self");
  };

  const toggleBlock = async (anydeskId, workerName) => {
    const isBlocked = blockedIds.has(anydeskId);
    try {
      if (isBlocked) {
        await axios.post(`${API}/remote-sessions/unblock/${anydeskId}`, {}, getAuthHeader());
        setBlockedIds(prev => { const next = new Set(prev); next.delete(anydeskId); return next; });
        toast.success(`${workerName || anydeskId} unblocked`, {
          description: "You'll receive alerts when they connect."
        });
      } else {
        await axios.post(`${API}/remote-sessions/block`, { anydesk_id: anydeskId }, getAuthHeader());
        setBlockedIds(prev => new Set(prev).add(anydeskId));
        toast.success(`${workerName || anydeskId} blocked`, {
          description: "To fully prevent reconnection, deny them in AnyDesk:",
          duration: 12000,
        });
        // Show AnyDesk instructions after a short delay
        setTimeout(() => {
          toast.info(
            "AnyDesk blocking steps:\n1. Open AnyDesk on the Mac\n2. Go to Settings → Security\n3. Under Access Control, find the blocked user's ID\n4. Click the ✕ or set to 'Deny'\n5. Click Apply",
            { duration: 15000 }
          );
        }, 500);
      }
    } catch (e) {
      toast.error(`Failed to ${isBlocked ? "unblock" : "block"}: ${e.response?.data?.detail || e.message}`);
    }
  };



  const toggleSilence = async () => {
    try {
      const res = await axios.post(`${API}/remote-sessions/silence-notifications`, {}, getAuthHeader());
      setSilenced(res.data.silenced);
      toast.success(res.data.silenced ? "Notifications silenced" : "Notifications resumed");
    } catch { toast.error("Failed to toggle notifications"); }
  };

  const handleShutdown = async () => {
    if (!confirm("Shut down AnyDesk on the Mac?\n\nThis kills AnyDesk for everyone until you restart it.")) return;
    try {
      await axios.post(`${API}/remote-sessions/shutdown-anydesk`, {}, getAuthHeader());
      toast.success("AnyDesk shutting down");
      setShowShutdownModal(true);
      fetchData();
    } catch { toast.error("Failed to send shutdown command"); }
  };

  const handleRestartAnyDesk = async () => {
    if (!confirm("Restart AnyDesk on the Mac?")) return;
    try {
      await axios.post(`${API}/remote-sessions/restart-anydesk`, {}, getAuthHeader());
      toast.success("AnyDesk restarting");
    } catch { toast.error("Failed to send restart command"); }
  };

  const handleCloseSession = async (sessionId) => {
    try {
      await axios.post(`${API}/remote-sessions/close-session/${sessionId}`, {}, getAuthHeader());
      toast.success("Session marked as ended");
      fetchData();
    } catch { toast.error("Failed to close session"); }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!confirm("Delete this session record? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/remote-sessions/session/${sessionId}`, getAuthHeader());
      toast.success("Session deleted");
      fetchData();
    } catch { toast.error("Failed to delete session"); }
  };

  const handleExport = async () => {
    try {
      const dateParam = selectedDate ? `&date=${selectedDate}` : `&month=${month}`;
      const res = await axios.get(`${API}/remote-sessions/export?${dateParam.slice(1)}`, { ...getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `remote_sessions_${selectedDate || month}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const handleMergeHistorical = async () => {
    if (!confirm("Merge fragmented sessions? Sessions from the same AnyDesk ID with gaps ≤ 2 minutes will be combined into single sessions.")) return;
    try {
      const res = await axios.post(`${API}/remote-sessions/merge-historical`, {}, getAuthHeader());
      const d = res.data;
      // Also run cleanup for stale/stuck sessions
      const cleanup = await axios.post(`${API}/remote-sessions/cleanup-stale`, {}, getAuthHeader());
      const total = (d.merged_count || 0) + (cleanup.data.stale_closed || 0) + (cleanup.data.test_deleted || 0);
      if (total > 0) {
        toast.success(`Cleaned up: ${d.merged_count} merged, ${cleanup.data.stale_closed} stale closed, ${cleanup.data.test_deleted} test removed`);
        fetchData();
      } else {
        toast.info("Everything looks clean — nothing to merge or clean up");
      }
    } catch { toast.error("Cleanup failed"); }
  };

  const saveMapping = async (anydeskId) => {
    if (!mappingName.trim()) return;
    try {
      const emp = employees.find((e) => e.id === mappingEmployeeId);
      await axios.post(`${API}/remote-sessions/map`, {
        anydesk_id: anydeskId, worker_name: mappingName.trim(),
        employee_id: mappingEmployeeId || null, employee_email: emp?.email || null
      }, getAuthHeader());
      toast.success("Worker mapping saved");
      setMappingId(null); setMappingName(""); setMappingEmployeeId("");
      fetchData();
    } catch { toast.error("Failed to save"); }
  };

  const handleRemoveMapping = async (anydeskId) => {
    if (!confirm(`Remove the worker assignment for AnyDesk ID ${anydeskId}?`)) return;
    try {
      await axios.delete(`${API}/remote-sessions/map/${anydeskId}`, getAuthHeader());
      toast.success("Assignment removed");
      setMappingId(null); setMappingName(""); setMappingEmployeeId("");
      fetchData();
    } catch { toast.error("Failed to remove assignment"); }
  };

  const handleClearAlerts = async () => {
    const dateParam = selectedDate || null;
    const monthParam = !selectedDate ? month : null;
    const label = selectedDate ? `alerts for ${selectedDate}` : `alerts for ${month}`;
    if (!confirm(`Clear all ${label}? This cannot be undone.`)) return;
    try {
      const params = new URLSearchParams();
      if (dateParam) params.set("date", dateParam);
      else if (monthParam) params.set("month", monthParam);
      const res = await axios.delete(`${API}/remote-sessions/alerts?${params}`, getAuthHeader());
      toast.success(`Cleared ${res.data.deleted} alert(s)`);
      fetchAlerts();
    } catch { toast.error("Failed to clear alerts"); }
  };

  const handleDeleteAlert = async (dedupKey) => {
    try {
      await axios.delete(`${API}/remote-sessions/alert/${encodeURIComponent(dedupKey)}`, getAuthHeader());
      toast.success("Alert deleted");
      fetchAlerts();
    } catch { toast.error("Failed to delete alert"); }
  };

  const handleAssign = (anydeskId) => {
    // Pre-fill form if session already has a mapping
    const session = sessions.find(s => s.anydesk_id === anydeskId);
    setMappingId(anydeskId);
    if (session?.worker_name) {
      setMappingName(session.worker_name);
      setMappingEmployeeId(session.employee_id || "");
    } else {
      setMappingName("");
      setMappingEmployeeId("");
    }
  };

  const isActive = (s) => !s.ended_at && s.duration_seconds === null;
  const activeCount = sessions.filter(isActive).length;

  // Group sessions by LOCAL day (not UTC)
  const grouped = useMemo(() => {
    const filtered = sessions.filter((s) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return [s.worker_name, s.alias, s.anydesk_id, s.host].some((v) => v && v.toLowerCase().includes(q));
    });
    const groups = {};
    for (const s of filtered) {
      const day = s.started_at ? localDateStr(new Date(s.started_at)) : "";
      if (!groups[day]) groups[day] = [];
      groups[day].push(s);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [sessions, search]);

  const totalFiltered = grouped.reduce((sum, [, items]) => sum + items.length, 0);

  return (
    <div className="min-h-screen bg-[#0F0F23]" data-testid="remote-sessions-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0F0F23]/95 backdrop-blur-md border-b border-white/10 px-4 pb-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <div className="max-w-3xl mx-auto space-y-2">
          {/* Top row: nav + title + utility icons */}
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/admin")} className="text-white/70 hover:text-white p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="remote-sessions-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-gradient-to-br from-[#6366F1] to-[#4F46E5] rounded-lg flex items-center justify-center shrink-0">
              <Monitor className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-white font-bold text-base leading-tight">Remote Sessions</h1>
              <p className="text-white/50 text-[11px]">
                {activeCount > 0 && <span className="text-emerald-400 font-medium">{activeCount} active now</span>}
                {activeCount === 0 && "AnyDesk session history"}
                {lastUpdated && (
                  <span className="text-white/30 ml-2">
                    updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                )}
              </p>
            </div>
            {/* Watcher health indicator */}
            {watcherHealth !== null && (
              <div
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium ${
                  watcherHealth.online
                    ? "bg-emerald-500/15 text-emerald-400"
                    : "bg-red-500/15 text-red-400 animate-pulse"
                }`}
                title={watcherHealth.online
                  ? `Watcher online (${watcherHealth.host}) — last heartbeat ${watcherHealth.seconds_ago}s ago`
                  : `Watcher OFFLINE (${watcherHealth.host || 'unknown'}) — ${watcherHealth.seconds_ago ? `last seen ${Math.round(watcherHealth.seconds_ago / 60)}min ago` : 'no heartbeat received'}`
                }
                data-testid="watcher-health-indicator"
              >
                {watcherHealth.online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {watcherHealth.online ? "Watcher Online" : "Watcher Offline"}
              </div>
            )}
            <button
              onClick={toggleSilence}
              className={`p-1.5 rounded-lg transition-colors ${
                silenced
                  ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
                  : "text-white/50 hover:text-white hover:bg-white/10"
              }`}
              title={silenced ? "Notifications silenced — tap to resume" : "Silence notifications"}
              data-testid="silence-notifications-btn"
            >
              {silenced ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            </button>
            <button onClick={handleMergeHistorical} className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition-colors" title="Merge fragmented sessions" data-testid="merge-sessions-btn">
              <Merge className="w-4 h-4" />
            </button>
            <button onClick={handleExport} className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition-colors" title="Export CSV" data-testid="export-csv-btn">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={() => { fetchData(); fetchAlerts(); }} disabled={loading} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors" data-testid="remote-sessions-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          {/* Second row: Connect + Shutdown + Restart buttons */}
          <div className="flex items-center gap-2">
            {connectInfo.anydesk_address ? (
              <button
                onClick={() => openAnyDesk(connectInfo.anydesk_address)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-500/20 border border-indigo-500/40 text-indigo-300 text-xs font-semibold hover:bg-indigo-500/30 transition-colors"
                data-testid="connect-anydesk-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Connect ({connectInfo.anydesk_address})
              </button>
            ) : (
              <button
                onClick={() => { setEditAddress(""); setEditLabel(""); setShowConnectSetup(true); }}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 text-white/40 text-xs font-semibold hover:bg-white/10 hover:text-white/60 transition-colors"
                data-testid="setup-connect-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Set Up Quick Connect
              </button>
            )}
            {connectInfo.anydesk_address && (
              <button
                onClick={() => { setEditAddress(connectInfo.anydesk_address); setEditLabel(connectInfo.anydesk_password || ""); setShowConnectSetup(true); }}
                className="p-2 rounded-lg text-white/20 hover:text-white/60 hover:bg-white/10 transition-colors"
                data-testid="edit-connect-btn"
                title="Edit AnyDesk address"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={handleShutdown} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 text-xs font-semibold hover:bg-red-500/25 transition-colors" data-testid="shutdown-anydesk-btn">
              <ShieldBan className="w-3.5 h-3.5" /> Shutdown
            </button>
            <button onClick={handleRestartAnyDesk} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/25 transition-colors" data-testid="restart-anydesk-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Restart
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-4 pb-3 space-y-3">
        {/* Silenced banner */}
        {silenced && (
          <div className="flex items-center gap-2.5 rounded-xl px-4 py-2.5 bg-amber-500/10 border border-amber-500/30" data-testid="silenced-banner">
            <BellOff className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-amber-200 text-sm flex-1">Notifications are silenced</span>
            <button
              onClick={toggleSilence}
              className="text-xs text-amber-300 hover:text-amber-100 font-medium px-2 py-1 rounded-lg hover:bg-amber-500/20 transition-colors"
              data-testid="resume-notifications-btn"
            >
              Resume
            </button>
          </div>
        )}
        {/* Live cross-check flags */}
        {flags.length > 0 && (
          <div className="space-y-2" data-testid="cross-check-flags">
            {flags.map((f, i) => (
              <div
                key={i}
                className={`flex items-start gap-2.5 rounded-xl px-4 py-3 border text-sm ${
                  f.severity === "alert" ? "bg-red-500/10 border-red-500/40 text-red-200"
                    : f.severity === "warning" ? "bg-amber-500/10 border-amber-500/40 text-amber-200"
                    : "bg-sky-500/10 border-sky-500/30 text-sky-200"
                }`}
                data-testid={`flag-${f.type}`}
              >
                {f.severity === "info" ? <Info className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                <span>{f.detail}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white/[0.05] p-1 rounded-lg" data-testid="session-tabs">
          <button
            onClick={() => setTab("sessions")}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
              tab === "sessions" ? "bg-indigo-500 text-white shadow" : "text-white/50 hover:text-white/80"
            }`}
            data-testid="tab-sessions"
          >
            <Monitor className="w-3.5 h-3.5" /> Sessions
          </button>
          <button
            onClick={() => setTab("alerts")}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
              tab === "alerts" ? "bg-amber-500 text-white shadow" : "text-white/50 hover:text-white/80"
            }`}
            data-testid="tab-alerts"
          >
            <Bell className="w-3.5 h-3.5" /> Alerts
            {alerts.length > 0 && <span className="text-[10px] bg-white/20 px-1.5 rounded-full">{alerts.length}</span>}
          </button>
        </div>

        {/* Month + Day navigation */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <MonthPicker value={month} onChange={(m) => { setMonth(m); setSelectedDate(null); }} />
            {selectedDate && (
              <button onClick={() => setSelectedDate(null)} className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1" data-testid="clear-date-btn">
                <CalendarDays className="w-3 h-3" /> Show full month
              </button>
            )}
          </div>
          <DayPills month={month} selectedDate={selectedDate} onSelect={setSelectedDate} />
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search worker, ID, or host..."
            className="pl-9 bg-white/[0.06] border-white/[0.12] text-white placeholder:text-white/30 rounded-lg h-9 text-sm"
            data-testid="remote-sessions-search"
          />
        </div>

        {/* Content */}
        {tab === "sessions" ? (
          loading ? (
            <p className="text-center text-white/40 py-12">Loading sessions...</p>
          ) : totalFiltered === 0 ? (
            <div className="text-center py-14 px-6" data-testid="sessions-empty">
              <Monitor className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/60 font-medium">
                {sessions.length === 0 ? "No sessions for this period" : "No sessions match your search"}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {grouped.map(([day, items]) => {
                const dayDate = new Date(day + "T12:00:00");
                const dayLabel = dayDate.toLocaleDateString("default", { weekday: "long", month: "short", day: "numeric" });
                const dayTotal = items.reduce((s, x) => s + (x.duration_seconds || 0), 0);
                return (
                  <div key={day} data-testid={`day-group-${day}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarDays className="w-3.5 h-3.5 text-indigo-400" />
                      <span className="text-white/80 text-sm font-semibold">{dayLabel}</span>
                      <span className="text-white/30 text-xs">{items.length} session{items.length !== 1 ? "s" : ""}</span>
                      {dayTotal > 0 && <span className="text-white/30 text-xs ml-auto">{formatDuration(dayTotal)} total</span>}
                    </div>
                    <div className="space-y-2">
                      {items.map((s, i) => (
                        <motion.div key={s.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.2) }}>
                          <SessionCard
                            s={s}
                            isActive={isActive(s)}
                            onAssign={handleAssign}
                            onRemoveMapping={handleRemoveMapping}
                            onClose={handleCloseSession}
                            onDelete={handleDeleteSession}
                            employees={employees}
                            mappingId={mappingId}
                            mappingName={mappingName}
                            setMappingName={setMappingName}
                            mappingEmployeeId={mappingEmployeeId}
                            setMappingEmployeeId={setMappingEmployeeId}
                            saveMapping={saveMapping}
                            setMappingId={setMappingId}
                            isBlocked={s.anydesk_id ? blockedIds.has(s.anydesk_id) : false}
                            onToggleBlock={toggleBlock}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Alerts tab */
          alerts.length === 0 ? (
            <div className="text-center py-14 px-6" data-testid="alerts-empty">
              <Bell className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/60 font-medium">No alerts for this period</p>
              <p className="text-white/40 text-sm mt-1">Cross-check alerts will appear here when flagged</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-end">
                <button
                  onClick={handleClearAlerts}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/25 text-red-300 text-xs font-medium hover:bg-red-500/20 transition-colors"
                  data-testid="clear-all-alerts-btn"
                >
                  <Trash2 className="w-3 h-3" /> Clear All
                </button>
              </div>
              {alerts.map((a, i) => {
                const typeLabel = a.type === "clocked_in_no_session" ? "Clocked in, no session"
                  : a.type === "session_no_clock_in" ? "Session, no clock-in"
                  : a.type === "blocked_connection" ? "Blocked ID connected"
                  : a.type === "unmapped_connection" ? "Unmapped ID connected"
                  : a.type || "Alert";
                const severity = a.type === "blocked_connection" ? "critical"
                  : a.type === "session_no_clock_in" ? "alert"
                  : "warning";
                return (
                  <motion.div
                    key={a.dedup_key || i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.2) }}
                    className={`rounded-xl px-4 py-3 border text-sm ${
                      severity === "critical" ? "bg-red-600/15 border-red-500/50" : severity === "alert" ? "bg-red-500/10 border-red-500/30" : "bg-amber-500/10 border-amber-500/30"
                    }`}
                    data-testid={`alert-item-${i}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${severity === "critical" ? "text-red-400" : severity === "alert" ? "text-red-400" : "text-amber-400"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            severity === "critical" ? "bg-red-500/30 text-red-200" : severity === "alert" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"
                          }`}>{typeLabel}</span>
                          <span className="text-white/30 text-xs ml-auto">{formatDT(a.sent_at)}</span>
                          {a.dedup_key && (
                            <button
                              onClick={() => handleDeleteAlert(a.dedup_key)}
                              className="text-white/20 hover:text-red-300 transition-colors p-0.5"
                              data-testid={`delete-alert-${i}`}
                              title="Delete this alert"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <p className={`mt-1 ${severity === "critical" ? "text-red-200" : severity === "alert" ? "text-red-200" : "text-amber-200"}`}>{a.detail}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Quick Connect setup/edit modal */}
      {showConnectSetup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowConnectSetup(false)}>
          <div className="bg-[#1a1a3a] border border-indigo-500/30 rounded-2xl p-5 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="connect-setup-modal">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-500/15 flex items-center justify-center shrink-0">
                <ExternalLink className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-base">Quick Connect Setup</h3>
                <p className="text-white/50 text-sm mt-1">
                  Update the AnyDesk address and password. This also updates what employees see.
                </p>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="text-white/60 text-xs font-medium mb-1 block">AnyDesk Address</label>
                    <Input
                      value={editAddress}
                      onChange={(e) => setEditAddress(e.target.value)}
                      placeholder="e.g. 1 396 262 135"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30"
                      data-testid="connect-address-input"
                      onKeyDown={(e) => e.key === "Enter" && saveHostAddress()}
                      autoFocus
                    />
                    <p className="text-white/30 text-[11px] mt-1">Find this in AnyDesk → "This Desk" on your work computer</p>
                  </div>
                  <div>
                    <label className="text-white/60 text-xs font-medium mb-1 block">AnyDesk Password</label>
                    <Input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      placeholder="Connection password"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30"
                      data-testid="connect-password-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button
                onClick={saveHostAddress}
                disabled={!editAddress.trim()}
                className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-40"
                data-testid="connect-save-btn"
              >
                <Check className="w-4 h-4 mr-1.5" /> Save
              </Button>
              <Button
                onClick={() => setShowConnectSetup(false)}
                variant="ghost"
                className="text-white/60 hover:text-white"
                data-testid="connect-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Shutdown instructions modal */}
      {showShutdownModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowShutdownModal(false)}>
          <div className="bg-[#1a1a3a] border border-red-500/30 rounded-2xl p-5 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="shutdown-instructions-modal">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
                <ShieldBan className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-base">AnyDesk Shut Down</h3>
                <p className="text-white/60 text-sm mt-1.5 leading-relaxed">
                  AnyDesk has been killed. <span className="text-red-200 font-semibold">All remote access is off.</span>
                </p>

                {/* Show who was connected */}
                {sessions.filter(s => !s.ended_at && s.duration_seconds === null).length > 0 && (
                  <div className="mt-3">
                    <p className="text-white/70 text-xs font-semibold mb-1.5">Users who were connected:</p>
                    <div className="space-y-1">
                      {sessions.filter(s => !s.ended_at && s.duration_seconds === null).map((s, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white/[0.06] rounded-lg px-3 py-2">
                          <span className="text-white text-sm font-medium">{s.worker_name || s.alias || "Unknown"}</span>
                          {s.anydesk_id && <span className="text-white/40 font-mono text-xs">{s.anydesk_id}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-amber-300/90 text-sm mt-3 leading-relaxed font-semibold">
                  To deny a user access before restarting:
                </p>
                <div className="mt-2 bg-white/[0.06] rounded-lg px-3 py-2.5 text-xs text-white/80 space-y-1.5">
                  <p>1. Open <span className="text-indigo-300 font-semibold">AnyDesk → Settings → Security</span></p>
                  <p>2. Turn on <span className="text-indigo-300 font-semibold">Access Control List</span></p>
                  <p>3. Add <span className="text-emerald-300 font-semibold">your own AnyDesk ID</span> to the list (so you don't lock yourself out)</p>
                  <p>4. <span className="text-red-300 font-semibold">Do NOT add</span> the person you want to deny — only IDs on the list can connect</p>
                </div>
                <p className="text-white/40 text-xs mt-2">
                  Also consider changing your unattended access password if the denied person had it.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowShutdownModal(false)}
              className="w-full mt-4 bg-white/10 hover:bg-white/15 text-white border border-white/20"
              data-testid="shutdown-modal-dismiss"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
